import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouteMatch } from '@packages/router-dom';
import { modules, modulesMetaData } from '../../../../ReactSharedScripts/components/CrossModuleLink/modules';
import { useIsListPage } from '../../../../ReactSharedScripts/hooks/useIsListPage';
import { hasCookiePortal } from '../../../../ReactSharedScripts/modules/app/components/CookieAccept/utils';
import { selectHasActivePositionOverlay } from '../../../../ReactSharedScripts/modules/app/store/selectors';
import { useErrorPageContext } from '../../../../ReactSharedScripts/modules/customPage/error';
import {
	useListSidebarProvider,
} from '../../../../ReactSharedScripts/modules/list/components/ListSidebar/useListSidebarProvider';
import { hasNew2Sidebar } from '../../../../ReactSharedScripts/modules/list/components/ListSidebar/utils';
import { usePublicDocumentListLogout } from '../../../../ReactSharedScripts/modules/publicDocument/list/hooks';
import { selectIsSidebarExpanded } from '../../../../ReactSharedScripts/modules/sidebar/store';
import { hasNewSidebar } from '../../../../ReactSharedScripts/modules/sidebar/utils';
import { useUserPageContextState } from '../../../../ReactSharedScripts/modules/user';
import { USER_LOGIN_PATH } from '../../../../ReactSharedScripts/utils/const';
import { getOffsetTop } from '../../../../ReactSharedScripts/utils/renderProps/OverlayPosition/utils';
import { useToolNavigationExpandedProvider } from '../toolNavigation/components/ToolNavigationExpandedProvider';
import HeaderLayout from './components/HeaderLayout';
import './styles/global.css';

const Header = memo(() => {
	// const { pathname } = useLocation();
	const { isInitialized: isInitializedUserContext, isAuthenticated, hasAcceptedCookie } = useUserPageContextState();
	const { routeError } = useErrorPageContext();
	const loginPageMatch = useRouteMatch({ path: USER_LOGIN_PATH });
	// const isNewModule = useIsNewModule(pathname);
	const isLoginPage = loginPageMatch && loginPageMatch.isExact;

	const isSidebarExpanded = useSelector(selectIsSidebarExpanded);
	const hasActivePositionOverlay = useSelector(selectHasActivePositionOverlay);
	const { isExpanded: isToolNavigationExpanded } = useToolNavigationExpandedProvider();
	const { isExpanded } = useListSidebarProvider();
	const { isPublicDocumentListPage, handlePublicDocumentListLogout } = usePublicDocumentListLogout();
	let isRightSidebarOpen = false;

	if (hasNewSidebar()) {
		isRightSidebarOpen = hasNew2Sidebar() ? isExpanded : (isSidebarExpanded as boolean);
	}

	const hasExpandedSidebar = isRightSidebarOpen && hasActivePositionOverlay;
	const headerHasExpandedView = hasActivePositionOverlay && isToolNavigationExpanded && isAuthenticated;
	const headerHasCollapsedView = hasActivePositionOverlay && !isToolNavigationExpanded;
	const dashboardPageMatch = useRouteMatch({
		path: `/${modules.DASHBOARD}/${modulesMetaData[modules.DASHBOARD].index}`,
	});

	const showLoginPageLanguages = !isAuthenticated;
	const showEndSessionButton = isPublicDocumentListPage && routeError?.statusCode !== 440 && !isAuthenticated;
	const showLoginButton = !isLoginPage && !isAuthenticated && !showEndSessionButton;
	const showSpinner = isAuthenticated && !isInitializedUserContext;
	const showUserMenu = isAuthenticated && isInitializedUserContext;
	const isListPage = useIsListPage();
	const showFullName = dashboardPageMatch !== null && dashboardPageMatch.isExact;
	const headerInlineStyles = useMemo(() => {
		const styles = {};
		const offsetTop = getOffsetTop();
		const cookieAcceptHeight = parseInt(document.body.style.paddingTop, 10);
		const maintenancePortal = document.querySelector('#maintenance-portal');
		const maintenancePortalRect = maintenancePortal?.getBoundingClientRect();

		if (
			hasActivePositionOverlay &&
			hasCookiePortal() &&
			!hasAcceptedCookie &&
			!Number.isNaN(cookieAcceptHeight) &&
			offsetTop < cookieAcceptHeight
		) {
			styles['top'] = `${cookieAcceptHeight - offsetTop}px`;
		}

		if (
			hasActivePositionOverlay &&
			maintenancePortal
		) {
			styles['top'] = `${maintenancePortalRect.height}px`;
		}

		return styles;
	}, [hasAcceptedCookie, hasActivePositionOverlay]);
	const headerProps = {
		isAuthenticated,
		isLoginPage,
		hasActivePositionOverlay,
		isToolNavigationExpanded,
		hasAcceptedCookie,
		hasExpandedSidebar,
		headerHasExpandedView,
		headerHasCollapsedView,
		showLoginPageLanguages,
		showLoginButton,
		showSpinner,
		showUserMenu,
		isListPage,
		headerInlineStyles,
		showFullName,
		showEndSessionButton,
		handlePublicDocumentListLogout,
	};

	return <HeaderLayout {...headerProps} />;
});

export default Header;

Header.displayName = 'Header';
